import { useEffect, useState } from "react";
import { Button, Dialog, TextAreaField } from "@leylion/design-system";
import { PlatformDiagnosticAlert } from "./PlatformDiagnosticAlert";
import type { PlatformDiagnostic, PlatformLanguage } from "./platform-diagnostics";

export function PlatformConfirmationDialog({
  open,
  language,
  title,
  description,
  target,
  action,
  impact,
  sessionImpact,
  confirmLabel,
  destructive = false,
  busy,
  diagnostic,
  onConfirm,
  onClose
}: {
  open: boolean;
  language: PlatformLanguage;
  title: string;
  description: string;
  target: string;
  action: string;
  impact: string;
  sessionImpact?: string;
  confirmLabel: string;
  destructive?: boolean;
  busy: boolean;
  diagnostic: PlatformDiagnostic | null;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const ar = language === "ar";
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open, title, target]);

  const normalizedReason = reason.trim();
  const reasonError = reason.length > 160
    ? ar
      ? "الحد الأقصى للسبب 160 حرفًا."
      : "The reason is limited to 160 characters."
    : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      closeLabel={ar ? "إغلاق نافذة التأكيد" : "Close confirmation"}
      closeDisabled={busy}
      className="platform-confirmation-dialog"
      initialFocusSelector="textarea"
      footer={(
        <>
          <Button type="button" variant="secondary" disabled={busy} onClick={onClose}>
            {ar ? "إلغاء" : "Cancel"}
          </Button>
          <Button
            type="button"
            variant={destructive ? "danger" : "primary"}
            disabled={busy || !normalizedReason || Boolean(reasonError)}
            onClick={() => onConfirm(normalizedReason)}
          >
            {busy ? (ar ? "جارٍ التنفيذ…" : "Working…") : confirmLabel}
          </Button>
        </>
      )}
    >
      <dl className="platform-confirmation-dialog__summary">
        <div>
          <dt>{ar ? "الهدف" : "Target"}</dt>
          <dd><bdi dir="ltr">{target}</bdi></dd>
        </div>
        <div>
          <dt>{ar ? "الإجراء" : "Action"}</dt>
          <dd>{action}</dd>
        </div>
        <div>
          <dt>{ar ? "الأثر" : "Impact"}</dt>
          <dd>{impact}</dd>
        </div>
        {sessionImpact ? (
          <div>
            <dt>{ar ? "أثر الجلسات" : "Session impact"}</dt>
            <dd>{sessionImpact}</dd>
          </div>
        ) : null}
      </dl>

      <TextAreaField
        autoFocus
        required
        maxLength={160}
        rows={4}
        label={ar ? "سبب الإجراء" : "Action reason"}
        hint={ar
          ? `${reason.length}/160 · يُحفظ السبب في سجل التدقيق.`
          : `${reason.length}/160 · The reason is recorded in the audit trail.`}
        error={reasonError}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder={ar ? "اكتب سببًا واضحًا ومحددًا" : "Enter a clear, specific reason"}
      />

      {diagnostic ? (
        <PlatformDiagnosticAlert diagnostic={diagnostic} language={language} />
      ) : null}
    </Dialog>
  );
}
